// main.ts — boot + the game state machine (SPEC §11).
//
//   BOOT → L01…L15 (each: PRESENT → INPUT → JUDGING → VERDICT → TRANSITION)
//   strikes == 3 anywhere → FLAGGED → (appeal) → start of current act
//   L15 → CERTIFICATE → SHARE ⇄ CERTIFICATE → (verify another human → restart)
//
// Session state lives in memory. Traits + best quote survive FLAGGED appeals; strikes
// do not. Phase 0 wires the whole loop with placeholder levels + stub end screens, and
// a dev-only debug harness that can step/jump through every level and act.

import "./style.css";
import { applyAct, actOf, transitionTo, prefersReducedMotion, type Act } from "@/theme/acts.ts";
import { SuspicionMeter } from "@/engine/suspicion.ts";
import { TraitAccumulator, buildCertificate, type Stamp } from "@/engine/traits.ts";
import { Chrome, el, delay } from "@/ui/chrome.ts";
import { prefetchMachineAnswer } from "@/engine/judge.ts";
import { renderCertificate, shareFail } from "@/ui/certificate.ts";
import { FLAGGED } from "@/content/voice.ts";
import { LEVELS } from "@/levels/registry.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

type Nav =
  | { type: "goto"; index: number }
  | { type: "restartAct" }
  | { type: "restartAll" };

class Game {
  private readonly stage: HTMLElement;
  private readonly reducedMotion = prefersReducedMotion();
  private readonly meter = new SuspicionMeter();
  private readonly traits = new TraitAccumulator();
  private chrome: Chrome;

  private index = 0;
  private renderedAct: Act | null = null;
  private ac = new AbortController();
  private pendingNav: Nav | null = null;
  private refreshDebug: (() => void) | null = null;

  constructor(stage: HTMLElement) {
    this.stage = stage;
    this.chrome = new Chrome(stage, this.reducedMotion);
    this.chrome.bindMeter(this.meter);
    if (import.meta.env.DEV) this.mountDebug();
  }

  async start(): Promise<void> {
    // Dev-only boot override: ?level=N jumps straight to level N (0-based) for
    // screenshotting / iterating on a single act without stepping the harness.
    if (import.meta.env.DEV) {
      const raw = new URLSearchParams(location.search).get("level");
      if (raw !== null) {
        const n = Number.parseInt(raw, 10);
        if (Number.isFinite(n)) this.index = clamp(n, 0, LEVELS.length - 1);
      }
    }
    applyAct(actOf(this.index));
    this.renderedAct = actOf(this.index);
    await this.loop();
  }

  private async loop(): Promise<void> {
    while (this.index < LEVELS.length) {
      const level = LEVELS[this.index]!;
      const act = level.act;
      this.refreshDebug?.();

      await this.enterAct(act);
      this.chrome.setTitle(this.titleForAct(act, level));
      this.chrome.setProgress(this.index);
      this.chrome.clearHost();

      this.ac = new AbortController();
      let result: LevelResult | null = null;
      try {
        result = await level.mount(this.chrome.host, this.contextFor(level));
      } catch (err) {
        if (isAbort(err)) {
          if (this.applyNav()) continue;
          // aborted without a queued nav (shouldn't happen) — just re-run this level
          continue;
        }
        throw err;
      }

      await this.processResult(level, result);

      // A queued nav (from debug) supersedes the natural advance.
      if (this.applyNav()) continue;
      this.index++;
    }

    await this.certificate();
  }

  /** Choreograph an act change the first time we render into a new act. */
  private async enterAct(act: Act): Promise<void> {
    if (this.renderedAct === act) {
      applyAct(act);
      return;
    }
    const from = this.renderedAct;
    this.renderedAct = act;
    // Prefetch the machine's own answers at act start so they are on hand when the level
    // mounts; the round trip hides behind the transition (§10). L08 lands in Act III,
    // L10 in Act IV — both wired in Phase 4, but the prefetch belongs to the judge engine.
    if (act === 3) prefetchMachineAnswer("L08");
    else if (act === 4) prefetchMachineAnswer("L10");
    if (from === null) {
      applyAct(act);
      return;
    }
    await transitionTo(this.stage, from, act, this.reducedMotion);
    // Reduced-motion II→III gets its one line in lieu of the set-piece (§12).
    if (this.reducedMotion && from === 2 && act === 3) {
      await this.chrome.verdictBeat("The room is larger than it appears.", { hold: 1400 });
    }
  }

  private async processResult(level: Level, result: LevelResult): Promise<void> {
    this.traits.addMany(result.traits);
    this.traits.offerQuote(result.quote);
    this.traits.recordFacts(result.data);
    // Human-ness % inputs (§8): every advanced level is a pass; HUMAN verdicts count extra.
    if (result.pass) this.traits.recordPass();
    this.traits.recordVerdict(result.verdict);

    if (result.suspicionEvent) this.meter.suspicionEvent();

    // If the level asked the machine to speak but handled its own beat, `line` is unset.
    if (result.line) await this.chrome.verdictBeat(result.line);

    if (result.strike) {
      const reason = result.strikeReason ?? result.line ?? "anomaly detected";
      const flagged = this.meter.strike(level.id, reason);
      if (flagged) {
        await this.flagged();
        // flagged() sets pendingNav to the act restart; loop honors it.
      }
    }
  }

  private contextFor(level: Level): LevelContext {
    return {
      act: level.act,
      index: this.index,
      reducedMotion: this.reducedMotion,
      emitTrait: (t) => this.traits.add(t),
      verdictBeat: (line, opts) =>
        this.chrome.verdictBeat(line, opts?.pending ? { pending: opts.pending } : {}),
      session: {
        strikes: this.meter.strikes,
        traits: this.traits.snapshot().traits,
        bestQuote: this.traits.quote,
      },
      signal: this.ac.signal,
    };
  }

  private titleForAct(act: Act, level: Level): string {
    if (act <= 2) return "Verify you are human";
    if (act === 3) return "The test continues";
    if (act === 4) return "Confession";
    return level.id === "L15" ? "" : "";
  }

  // ── FLAGGED FOR REVIEW (§4) — Phase 0 stub screen ──────────────────

  private async flagged(): Promise<void> {
    const host = this.chrome.host;
    this.chrome.clearHost();

    const wrap = el("div", "flagged");

    const h = el("div", "flagged__title");
    h.textContent = FLAGGED.title;
    const sub = el("div", "flagged__sub");
    sub.textContent = FLAGGED.sub;

    const reportHead = el("div", "flagged__report-head");
    reportHead.textContent = FLAGGED.reportHeader;
    const report = el("div", "flagged__report");
    for (const r of this.meter.state.records) {
      const line = el("div", "flagged__record");
      line.textContent = `— ${r.reason}`;
      report.append(line);
    }

    const footer = el("div", "flagged__footer");
    footer.textContent = FLAGGED.footer;

    const actions = el("div", "flagged__actions");
    const share = el("button", "btn btn--danger-solid");
    share.textContent = FLAGGED.share;
    share.addEventListener("click", () =>
      shareFail(share, { reasons: this.meter.state.records.map((r) => r.reason) }),
    );
    const appeal = el("button", "btn");
    appeal.textContent = FLAGGED.appeal;
    actions.append(share, appeal);

    wrap.append(h, sub, reportHead, report, footer, actions);
    host.append(wrap);

    await new Promise<void>((resolve) => {
      appeal.addEventListener(
        "click",
        () => {
          // Resume at the start of the current act, strikes cleared (§4).
          this.meter.clearStrikes();
          this.pendingNav = { type: "goto", index: firstIndexOfAct(actOf(this.index)) };
          resolve();
        },
        { once: true },
      );
    });
  }

  // ── CERTIFICATE (§8) — the personalized artifact + share PNG ──────────

  private async certificate(): Promise<void> {
    await this.enterAct(5);
    this.chrome.setTitle("");
    this.chrome.clearHost();
    document.body.dataset.screen = "cert";

    const { traits, bestQuote } = this.traits.snapshot();
    const data = buildCertificate({
      traits, // personality traits only — L13 speed picks live in facts, not the trait counts
      speedTags: this.traits.fact<string[]>("speedTags") ?? [],
      circleScore: this.traits.fact<number>("circleScore") ?? null,
      exhibitA: bestQuote,
      stamp: (this.traits.fact<string>("stamp") as Stamp) ?? "SKEPTIC",
      passes: this.traits.passCount,
      humanVerdicts: this.traits.humanCount,
      strikes: this.meter.strikes,
    });

    renderCertificate(this.chrome.host, data, {
      onAgain: () => location.reload(),
    });
  }

  // ── nav plumbing ───────────────────────────────────────────────────

  /** If a nav is queued, apply it to `index` and clear it. Returns true if applied. */
  private applyNav(): boolean {
    const nav = this.pendingNav;
    if (!nav) return false;
    this.pendingNav = null;
    if (nav.type === "goto") this.index = clamp(nav.index, 0, LEVELS.length - 1);
    else if (nav.type === "restartAct") this.index = firstIndexOfAct(actOf(this.index));
    else if (nav.type === "restartAll") this.index = 0;
    return true;
  }

  private queueNav(nav: Nav): void {
    this.pendingNav = nav;
    this.ac.abort();
  }

  // ── dev debug harness ──────────────────────────────────────────────

  private mountDebug(): void {
    const bar = el("div", "debug");
    const id = el("span", "debug__id");
    const refreshId = () => {
      const lvl = LEVELS[this.index];
      id.textContent = lvl ? `${lvl.id} · Act ${lvl.act} · ${lvl.title}` : "—";
    };
    refreshId();
    this.refreshDebug = refreshId;
    this.meter.onChange(() => refreshId());

    const btn = (label: string, fn: () => void) => {
      const b = el("button");
      b.textContent = label;
      b.addEventListener("click", () => {
        fn();
        refreshId();
      });
      return b;
    };

    bar.append(
      btn("◀", () => this.queueNav({ type: "goto", index: Math.max(0, this.index - 1) })),
      id,
      btn("▶", () => this.queueNav({ type: "goto", index: this.index + 1 })),
      btn("↻act", () => this.queueNav({ type: "restartAct" })),
      btn("+strike", () => this.meter.strike("DBG", "debug strike")),
    );
    for (const a of [1, 2, 3, 4, 5] as Act[]) {
      bar.append(btn(`${romans[a]}`, () => this.queueNav({ type: "goto", index: firstIndexOfAct(a) })));
    }
    document.body.append(bar);
  }
}

// ── helpers ───────────────────────────────────────────────────────────

const romans: Record<Act, string> = { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V" };

function firstIndexOfAct(act: Act): number {
  return LEVELS.findIndex((l) => l.act === act);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── boot ──────────────────────────────────────────────────────────────

const stage = document.getElementById("stage");
if (!stage) throw new Error("no #stage element");
void delay(0).then(() => new Game(stage).start());
